import { Badge, Banner, Button, Input, Surface, Table, Text } from '@cloudflare/kumo'
import {
  CUSTOMER_INFO_ITEM_LABEL,
  productCategoryLabel,
  SAMPLE_OFFER_TYPE_LABEL,
  SAMPLE_PAYMENT_STATUS_LABEL,
  SAMPLE_WORK_STATUS_LABEL,
  type AdminSampleOrderView,
  type FactorySampleOrderView,
  type SampleCategorySummary,
  type AdminSampleMessageView,
  type SampleFactoryOption,
  type SampleOfferView,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '../components/page-header'
import { SampleOfferForm, SampleOrderCard } from '../components/sample-forms'
import { useGetToken } from '../lib/auth'
import { fmtInt, toMessage } from '../lib/format'
import { samplesApi, type SampleOrderEventView } from '../samples-api'

const money = (amount: number, currency: string) =>
  currency === 'KRW' ? `₩${fmtInt(Math.round(amount))}` : `$${amount.toFixed(2)}`
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('ko') : '—')

/**
 * [샘플 거래] (AI 중계 샘플, docs/design-ai-brokered-samples.md).
 * 위에서부터: 분류별 샘플 가능 공장 현황(빈 분류 = 영업 우선순위) → 공장 샘플 제안(공장 계정이 없는 공장 대행 편집) →
 * 주문 목록과 한 건 처리(공장 진행 대행 · 기한 연장 · 환불 검토 · 환불 요청). 환불 책임이 정해지지 않아 자동 환불은 없다
 */
export function SamplesPage() {
  const getToken = useGetToken()
  const [categories, setCategories] = useState<SampleCategorySummary[] | null>(null)
  const [offers, setOffers] = useState<SampleOfferView[] | null>(null)
  const [factories, setFactories] = useState<SampleFactoryOption[]>([])
  const [orders, setOrders] = useState<AdminSampleOrderView[] | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [factoryId, setFactoryId] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<{
    order: AdminSampleOrderView
    factoryView: FactorySampleOrderView
    events: SampleOrderEventView[]
  } | null>(null)
  const [note, setNote] = useState('')
  const [queue, setQueue] = useState<AdminSampleMessageView[]>([])
  const [replies, setReplies] = useState<Record<string, string>>({})
  const [days, setDays] = useState('3')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [c, o, f, r, q] = await Promise.all([
        samplesApi.categories(getToken),
        samplesApi.offers(getToken),
        samplesApi.factories(getToken),
        samplesApi.orders(getToken),
        samplesApi.messageQueue(getToken),
      ])
      setCategories(c.items)
      setOffers(o.items)
      setFactories(f.items)
      setOrders(r.items)
      setQueue(q.items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    }
  }, [getToken])
  useEffect(() => {
    void load()
  }, [load])
  useEffect(() => {
    if (!selected) return setDetail(null)
    samplesApi
      .order(getToken, selected)
      .then(setDetail)
      .catch((err: unknown) => setError(toMessage(err)))
  }, [getToken, selected, orders])

  const act = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      setNotice(done)
      setNote('')
      await load()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="샘플 거래"
        description="샘플을 주는 공장만 분류별로 모아요. 고객은 결제 전까지 공장을 익명으로 보고, 결제 뒤에 서로 공개돼요. 앱에 보이려면 [설정] › 샘플 거래를 켜야 해요."
      />
      {error && <Banner variant="error" title="처리하지 못했어요" description={error} />}
      {notice && <Banner variant="default" title={notice} />}

      <section className="flex flex-col gap-3" data-testid="sample-categories">
        <Text variant="heading">분류별 샘플 가능 공장</Text>
        <Text variant="secondary" size="sm">
          공장이 0 인 분류는 앱 결과 화면에 '샘플 받기'가 보이지 않고 '견적 받기'만 보여요. 영업
          우선순위로 쓰세요. 연결 수수료가 비어 있으면 무료 방식은 세지 않아요.
        </Text>
        <Surface className="overflow-x-auto rounded-lg p-0">
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>분류</Table.Head>
                <Table.Head>공장 수</Table.Head>
                <Table.Head>방식</Table.Head>
                <Table.Head>최저가</Table.Head>
                <Table.Head>최단 소요일</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {categories?.map((c) => (
                <Table.Row key={c.categoryId}>
                  <Table.Cell>{productCategoryLabel(c.categoryId, 'ko')}</Table.Cell>
                  <Table.Cell>
                    {c.factoryCount ? c.factoryCount : <Badge variant="secondary">없음</Badge>}
                  </Table.Cell>
                  <Table.Cell>
                    {c.offerTypes.map((t) => SAMPLE_OFFER_TYPE_LABEL.ko[t]).join(', ') || '—'}
                  </Table.Cell>
                  <Table.Cell>
                    {Object.entries(c.minAmount)
                      .map(([currency, amount]) => money(amount, currency))
                      .join(' · ') || '—'}
                  </Table.Cell>
                  <Table.Cell>{c.minLeadDays === null ? '—' : `${c.minLeadDays}일`}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Surface>
      </section>

      <section className="flex flex-col gap-3" data-testid="sample-offers">
        <div className="flex items-center justify-between">
          <Text variant="heading">공장 샘플 제안</Text>
          <Button variant="secondary" onClick={() => setEditing('new')}>
            새 제안
          </Button>
        </div>
        {editing === 'new' && (
          <Surface className="flex flex-col gap-3 rounded-lg p-4">
            {/* 공장은 [공장 목록]에서 고른다. 이름·지역·가능 분류도 그 목록 값이 쓰인다 */}
            <label className="flex flex-col gap-1 text-sm">
              공장 (공장 목록에서)
              <select
                className="rounded border border-gray-300 px-2 py-1"
                value={factoryId}
                onChange={(e) => setFactoryId(e.target.value)}
              >
                <option value="">공장을 고르세요</option>
                {factories.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.region ? ` · ${f.region}` : ''}
                    {f.portalLinked ? ' · 포털 계정 있음' : ''}
                  </option>
                ))}
              </select>
            </label>
            <SampleOfferForm
              lang="ko"
              offer={null}
              disabled={busy || !factoryId.trim()}
              onSave={(input) =>
                void act(async () => {
                  await samplesApi.saveOffer(getToken, { ...input, factoryId: factoryId.trim() })
                  setEditing(null)
                }, '제안을 저장했어요')
              }
            />
          </Surface>
        )}
        <Surface className="overflow-x-auto rounded-lg p-0">
          <Table>
            <Table.Body>
              {offers?.map((o) =>
                editing === o.id ? (
                  <Table.Row key={o.id}>
                    <Table.Cell colSpan={4}>
                      <SampleOfferForm
                        lang="ko"
                        offer={o}
                        disabled={busy}
                        onSave={(input) =>
                          void act(async () => {
                            await samplesApi.saveOffer(
                              getToken,
                              { ...input, factoryId: o.factoryId },
                              o.id,
                            )
                            setEditing(null)
                          }, '제안을 고쳤어요')
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  <Table.Row key={o.id}>
                    <Table.Cell>
                      {o.factoryName}{' '}
                      <span className="text-kumo-subtle text-xs">{o.factoryId}</span>
                    </Table.Cell>
                    <Table.Cell>{SAMPLE_OFFER_TYPE_LABEL.ko[o.offerType]}</Table.Cell>
                    <Table.Cell>
                      {o.anonRegion} ·{' '}
                      {o.categoryIds.map((id) => productCategoryLabel(id, 'ko')).join(', ')}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={o.status === 'active' ? 'primary' : 'secondary'}>
                        {o.status}
                      </Badge>{' '}
                      <Button variant="ghost" size="sm" onClick={() => setEditing(o.id)}>
                        고치기
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ),
              )}
            </Table.Body>
          </Table>
        </Surface>
      </section>

      <section className="flex flex-col gap-3" data-testid="sample-messages">
        <Text variant="heading">메시지 대기열</Text>
        <Text variant="secondary" size="sm">
          AI 가 고쳐 보내지 못했거나(실패·효능 표현), 고객이 AI 전송에 동의하지 않았거나, 연락처만
          있던 글이에요. 원안은 받는 쪽에 가지 않았어요. 받는 쪽에 갈 글을 적어 보내거나 막아
          주세요. 연락처·링크는 넣지 마세요.
        </Text>
        {queue.length === 0 && <Text size="sm">대기 중인 메시지가 없어요.</Text>}
        {queue.map((m) => (
          <Surface key={m.id} className="flex flex-col gap-2 rounded-lg p-4">
            <Text size="sm">
              {m.direction === 'factory_to_customer' ? '공장 → 고객' : '고객 → 공장'} ·{' '}
              {m.kind === 'counter_offer' ? '역제안 · ' : ''}사유 {m.reviewReason ?? m.status} ·
              주문 {m.orderId}
              {m.filterHits.length ? ` · 가린 것 ${m.filterHits.join(', ')}` : ''}
            </Text>
            <Text size="sm">원문: {m.original}</Text>
            <textarea
              className="rounded border p-2 text-sm"
              rows={3}
              value={replies[m.id] ?? m.filtered}
              onChange={(e) => setReplies({ ...replies, [m.id]: e.target.value })}
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                disabled={busy || !(replies[m.id] ?? m.filtered).trim()}
                onClick={() =>
                  void act(
                    () =>
                      samplesApi.messageAction(getToken, m.id, {
                        action: 'send',
                        text: replies[m.id] ?? m.filtered,
                      }),
                    '고쳐 보냈어요',
                  )
                }
              >
                이 글로 보내기
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () =>
                      samplesApi.messageAction(getToken, m.id, {
                        action: 'block',
                        note: '관리자 대기열에서 막음',
                      }),
                    '막았어요',
                  )
                }
              >
                막기
              </Button>
            </div>
          </Surface>
        ))}
      </section>

      <section className="flex flex-col gap-3" data-testid="sample-orders">
        <Text variant="heading">주문</Text>
        <Surface className="overflow-x-auto rounded-lg p-0">
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>접수</Table.Head>
                <Table.Head>공장</Table.Head>
                <Table.Head>방식</Table.Head>
                <Table.Head>상태</Table.Head>
                <Table.Head>금액</Table.Head>
                <Table.Head>기한</Table.Head>
                <Table.Head>남은 동의</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {orders?.map((o) => (
                <Table.Row
                  key={o.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(o.id === selected ? null : o.id)}
                >
                  <Table.Cell>{date(o.createdAt)}</Table.Cell>
                  <Table.Cell>{o.factoryName}</Table.Cell>
                  <Table.Cell>{SAMPLE_OFFER_TYPE_LABEL.ko[o.offerType]}</Table.Cell>
                  <Table.Cell>
                    {/* 결제와 업무를 나눠 보인다 (대표 결정 2차 D) */}
                    {SAMPLE_PAYMENT_STATUS_LABEL.ko[o.paymentStatus]} ·{' '}
                    {SAMPLE_WORK_STATUS_LABEL.ko[o.fulfilmentStatus]}{' '}
                    {o.overdueAt && <Badge variant="error">기한 초과</Badge>}
                  </Table.Cell>
                  <Table.Cell>{money(o.amount, o.currency)}</Table.Cell>
                  <Table.Cell>{date(o.dueAt)}</Table.Cell>
                  <Table.Cell>
                    {o.missingInfo.map((i) => CUSTOMER_INFO_ITEM_LABEL.ko[i]).join(', ') || '—'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Surface>
        {detail && (
          <Surface className="flex flex-col gap-4 rounded-lg p-4">
            <Text variant="heading">
              주문 {detail.order.id} · 고객 {detail.order.userId}
            </Text>
            <Text variant="secondary" size="sm">
              결제 {detail.order.provider ?? '—'} · 결제일 {date(detail.order.paidAt)} · 결제·환불
              돈 기록은 [사용자]의 결제·환불 기록에 있어요.
            </Text>
            <SampleOrderCard
              lang="ko"
              order={detail.factoryView}
              disabled={busy}
              onProgress={(input) =>
                void act(
                  () => samplesApi.adminProgress(getToken, detail.order.id, input),
                  '공장 대신 진행을 기록했어요',
                )
              }
            />
            <div className="flex flex-wrap items-end gap-2">
              <Input
                label="결정 메모 (감사 기록에 남아요)"
                value={note}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
              />
              <Input
                label="연장 일수"
                type="number"
                value={days}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDays(e.target.value)}
              />
              <Button
                variant="secondary"
                disabled={busy || !note.trim()}
                onClick={() =>
                  void act(
                    () =>
                      samplesApi.action(getToken, detail.order.id, {
                        action: 'extend',
                        days: Number(days) || 1,
                        note,
                      }),
                    '기한을 늘렸어요',
                  )
                }
              >
                기한 연장
              </Button>
              <Button
                variant="secondary"
                disabled={busy || !note.trim()}
                onClick={() =>
                  void act(
                    () =>
                      samplesApi.action(getToken, detail.order.id, {
                        action: 'refund_review',
                        note,
                      }),
                    '환불 검토로 돌렸어요',
                  )
                }
              >
                환불 검토
              </Button>
              <Button
                variant="destructive"
                disabled={busy || !note.trim()}
                onClick={() =>
                  void act(
                    () => samplesApi.action(getToken, detail.order.id, { action: 'refund', note }),
                    '결제사에 환불을 요청했어요. 환불이 확정되면 주문이 환불로 바뀌어요.',
                  )
                }
              >
                환불 요청
              </Button>
            </div>
            <div className="flex flex-col gap-1">
              <Text variant="secondary" size="sm">
                감사 기록
              </Text>
              {detail.events.map((e) => (
                <Text key={e.id} size="sm">
                  {new Date(e.createdAt).toLocaleString('ko')} · {e.kind} · {e.actorRole}{' '}
                  {e.actorId}
                </Text>
              ))}
            </div>
          </Surface>
        )}
      </section>
    </div>
  )
}
